import {
  LIFECYCLE_ACTIONS,
  STATUS_ACTIONS,
  useEventLifecycleMutation,
  type LifecycleAction,
} from '@/components/events/eventLifecycle';
import { useEventReadiness } from '@/components/events/useEventReadiness';
import type { Event } from '@/config/objects/events';
import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@startupquickstart/frontend';
import { CircleAlert } from 'lucide-react';
import { useState, type MouseEvent } from 'react';

/** Actions that must pass the readiness pre-flight before confirming. */
const PREFLIGHT_ACTIONS: readonly LifecycleAction[] = ['publish', 'start'];

interface EventLifecycleButtonsProps {
  event: Pick<Event, 'id' | 'status'>;
  /** Cap how many actions to render (list rows pass 1 for the primary only). */
  limit?: number;
  size?: 'sm' | 'default';
  /**
   * Override the button style for non-destructive actions. List rows pass
   * `'outline'` so the inline action sits quietly next to the status badge
   * instead of competing with it as a filled primary button. Destructive
   * actions (End Event) always keep their destructive styling.
   */
  variantOverride?: 'outline';
  className?: string;
}

/**
 * Status-aware lifecycle action buttons + their shared confirm dialog.
 *
 * Publish/Start run a readiness pre-flight first: error-level checks open a
 * blocking dialog listing what to fix; warnings are folded into the normal
 * confirm copy. Reused by the detail-page header (all actions), the events
 * list rows, and the dashboard.
 *
 * @param props - Target event, optional render cap, size, and class name.
 */
export function EventLifecycleButtons({
  event,
  limit,
  size = 'default',
  variantOverride,
  className,
}: EventLifecycleButtonsProps) {
  const [confirmAction, setConfirmAction] = useState<LifecycleAction | null>(null);
  const [blocked, setBlocked] = useState(false);
  const mutation = useEventLifecycleMutation(event.id);

  const actions = STATUS_ACTIONS[event.status] ?? [];
  const shown = typeof limit === 'number' ? actions.slice(0, limit) : actions;

  // Pre-fetch readiness whenever a pre-flight action is available.
  const needsPreflight = shown.some((action) => PREFLIGHT_ACTIONS.includes(action));
  const readiness = useEventReadiness(event.id, needsPreflight);

  if (shown.length === 0) return null;

  const readinessData = readiness.data?.data;
  const errors = readinessData?.checks.filter((check) => check.level === 'error') ?? [];
  const warnings = readinessData?.checks.filter((check) => check.level === 'warning') ?? [];

  const handleActionClick = (action: LifecycleAction) => {
    if (PREFLIGHT_ACTIONS.includes(action) && readinessData && !readinessData.ready) {
      setBlocked(true);
      return;
    }
    setConfirmAction(action);
  };

  const current = confirmAction ? LIFECYCLE_ACTIONS[confirmAction] : null;
  const showWarnings =
    confirmAction !== null && PREFLIGHT_ACTIONS.includes(confirmAction) && warnings.length > 0;

  const confirmDescription = current
    ? showWarnings
      ? `${current.confirmDescription}\n\nHeads up: ${warnings.map((warning) => warning.message).join(' · ')}`
      : current.confirmDescription
    : '';

  return (
    <div className={className}>
      {shown.map((action) => {
        const config = LIFECYCLE_ACTIONS[action];
        const Icon = config.icon;
        // Destructive actions keep their styling; everything else honours the
        // caller's override (list rows quiet the primary action to outline).
        const buttonVariant =
          config.variant === 'destructive'
            ? 'destructive'
            : (variantOverride ?? config.variant ?? 'default');
        return (
          <Button
            key={action}
            type="button"
            size={size}
            variant={buttonVariant}
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              // Row buttons live inside a clickable table row — don't navigate.
              e.stopPropagation();
              handleActionClick(action);
            }}
          >
            <Icon className="h-4 w-4" />
            {config.label}
          </Button>
        );
      })}

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(open) => !open && setConfirmAction(null)}
        title={current?.confirmTitle ?? ''}
        description={confirmDescription}
        confirmText={current?.label ?? 'Confirm'}
        variant={current?.variant === 'destructive' ? 'destructive' : 'default'}
        loading={mutation.isPending}
        onConfirm={() => {
          if (confirmAction) {
            mutation.mutate(confirmAction, { onSuccess: () => setConfirmAction(null) });
          }
        }}
      />

      {/* Readiness block — error-level checks must be fixed before launch */}
      <Dialog open={blocked} onOpenChange={setBlocked}>
        <DialogContent onClick={(e: MouseEvent) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>This hunt isn’t ready yet</DialogTitle>
            <DialogDescription>
              Fix the following before launching — each one links from the “Ready to launch?”
              checklist on the event overview.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2">
            {errors.map((check) => (
              <li key={check.id} className="flex items-start gap-2.5 text-sm">
                <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <span className="text-foreground">
                  {check.message}
                  {check.count > 1 ? (
                    <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                      ×{check.count}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlocked(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
